import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {KTSVG} from '../../../helpers'
import {useSearchParams} from 'react-router-dom'
import {DEFAULT_PAGE} from '../../../constants'
import {isEmpty} from 'lodash'
import {Button} from '../form/Button'
import clsx from 'clsx'
import {toast} from 'react-toastify'
import {useIntl} from 'react-intl'

type Props = {
  placeholder?: string
  className?: string
  keySearch?: string
  dataAttribute?: string
  debounceMs?: number
}

const InputSearch: FC<Props> = ({
  placeholder = '',
  className = '',
  keySearch = 'search_text',
  dataAttribute,
  debounceMs,
}) => {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchText, setSearchText] = useState(searchParams.get('search_text') || '')
  const [isFocused, setIsFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const isMounted = useRef(false)

  const syncTextareaHeight = useCallback(() => {
    if (!textareaRef.current) return
    const target = textareaRef.current
    target.style.height = 'auto'
    target.style.height = `${Math.max(44, Math.min(target.scrollHeight, 200))}px`
  }, [])

  const handleSearch = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (isEmpty(searchText)) {
          searchParams.delete(keySearch)
          searchParams.delete('page_size')
        } else {
          const trackingNumbers = searchText
            .split(',')
            .map((tn) => tn.trim())
            .filter((tn) => tn.length > 0)
          const count = trackingNumbers.length

          if (count > 100) {
            toast.error(intl.formatMessage({id: 'TOO_MANY_TRACKING_NUMBERS'}))
            return
          }

          searchParams.set(keySearch, searchText)

          let pageSize = 10
          if (count > 50) {
            pageSize = 100
          } else if (count > 25) {
            pageSize = 50
          } else if (count > 10) {
            pageSize = 25
          }

          if (count > 10) {
            searchParams.set('page_size', pageSize.toString())
          } else {
            searchParams.delete('page_size')
          }
        }
        searchParams.set('page', DEFAULT_PAGE)
        setSearchParams(searchParams)
      }
    },
    [intl, keySearch, searchParams, searchText, setSearchParams]
  )

  useEffect(() => {
    setSearchText(searchParams.get(keySearch) || '')
  }, [keySearch, searchParams])

  // Sync textarea height when searchText changes (e.g. cleared on tab switch)
  useEffect(() => {
    syncTextareaHeight()
  }, [searchText, syncTextareaHeight])

  useEffect(() => {
    if (debounceMs === undefined) return
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    const timer = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams.toString())
      if (isEmpty(searchText)) {
        newParams.delete(keySearch)
      } else {
        newParams.set(keySearch, searchText)
      }
      newParams.set('page', DEFAULT_PAGE)
      setSearchParams(newParams)
    }, debounceMs)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, debounceMs])

  return (
    <div
      className={clsx('input-search-container position-relative mw-100', {
        active: isFocused || searchText,
      })}
    >
      <KTSVG
        path='/media/gori/magnify.svg'
        className='svg-icon-1 position-absolute top-0 ms-4 mt-3'
      />
      <textarea
        ref={textareaRef}
        autoComplete='off'
        className={clsx('form-control form-control-solid px-14 input-search-textarea', className)}
        placeholder={placeholder}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyDown={(e) => handleSearch(e)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        value={searchText}
        rows={1}
        onInput={syncTextareaHeight}
        {...(dataAttribute && {'data-search-input': dataAttribute})}
      />
      {searchText && (
        <Button
          event={() => {
            setSearchText('')
            searchParams.delete(keySearch)
            setSearchParams(searchParams)
            syncTextareaHeight()
          }}
          classNameDiv='d-flex justify-content-center align-items-center'
          className='text-hover-dark border-0 bg-transparent rounded-circle py-1 position-absolute top-0 end-0 me-4 mt-2'
        >
          <KTSVG
            path='/media/gori/orders/delete.svg'
            className='opacity-50 m-0'
            svgClassName='mh-10px'
          />
        </Button>
      )}
    </div>
  )
}

export {InputSearch}
